import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  loginApplicant,
  loginApplicantOtp,
  registerApplicant,
  requestApplicantOtp,
} from "@/lib/api";

export const ApplicantAuthPage = ({ onLogin }) => {
  const [tab, setTab] = useState("login");
  const [registerForm, setRegisterForm] = useState({
    full_name: "",
    mobile_number: "",
    email: "",
    password: "",
  });

  const [loginForm, setLoginForm] = useState({ mobile_number: "", password: "" });
  const [otpMobile, setOtpMobile] = useState("");
  const [otpCode, setOtpCode] = useState("");
  const [otpIssued, setOtpIssued] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleRegister = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await registerApplicant(registerForm);
      toast.success("Registration successful. Please login.");
      setTab("login");
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  const handleLoginPassword = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const data = await loginApplicant(loginForm);
      onLogin({
        token: data.token,
        role: data.role,
        user_id: data.user_id,
        identifier: data.identifier,
        must_change_password: data.must_change_password,
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  const handleRequestOtp = async () => {
    if (!otpMobile.trim()) {
      toast.error("Enter mobile number first");
      return;
    }
    setLoading(true);
    try {
      const data = await requestApplicantOtp({ mobile_number: otpMobile.trim() });
      setOtpIssued(true);
      toast.success(`OTP sent. (Test OTP: ${data.otp_code})`);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "OTP request failed");
    } finally {
      setLoading(false);
    }
  };

  const handleOtpLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      const data = await loginApplicantOtp({
        mobile_number: otpMobile.trim(),
        otp_code: otpCode.trim(),
      });
      onLogin({
        token: data.token,
        role: data.role,
        user_id: data.user_id,
        identifier: data.identifier,
        must_change_password: data.must_change_password,
      });
    } catch (error) {
      toast.error(error?.response?.data?.detail || "OTP login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page-bg" data-testid="applicant-auth-page">
      <div className="auth-card wide" data-testid="applicant-auth-card">
        <div className="auth-tab-row" data-testid="applicant-auth-tabs">
          {[
            ["login", "Login"],
            ["otp", "Login with OTP"],
            ["register", "Register"],
          ].map(([key, label]) => (
            <button
              key={key}
              className={`auth-tab ${tab === key ? "active" : ""}`}
              onClick={() => setTab(key)}
              data-testid={`applicant-tab-${key}`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "login" ? (
          <form onSubmit={handleLoginPassword} className="auth-form" data-testid="applicant-login-form">
            <h1 className="auth-title">Applicant Login</h1>
            <p className="auth-subtitle">Login with mobile and password</p>

            <label>Mobile Number</label>
            <Input
              value={loginForm.mobile_number}
              onChange={(event) => setLoginForm((prev) => ({ ...prev, mobile_number: event.target.value }))}
              className="auth-input"
              data-testid="applicant-login-mobile"
            />

            <label>Password</label>
            <Input
              type="password"
              value={loginForm.password}
              onChange={(event) => setLoginForm((prev) => ({ ...prev, password: event.target.value }))}
              className="auth-input"
              data-testid="applicant-login-password"
            />

            <Button className="auth-submit-btn" type="submit" disabled={loading} data-testid="applicant-login-submit">
              {loading ? "Please wait..." : "Login"}
            </Button>
          </form>
        ) : null}

        {tab === "otp" ? (
          <form onSubmit={handleOtpLogin} className="auth-form" data-testid="applicant-otp-form">
            <h1 className="auth-title">Applicant OTP Login</h1>
            <p className="auth-subtitle">Request OTP on registered mobile</p>

            <label>Mobile Number</label>
            <Input
              value={otpMobile}
              onChange={(event) => setOtpMobile(event.target.value)}
              className="auth-input"
              data-testid="applicant-otp-mobile"
            />
            <Button
              type="button"
              className="auth-secondary-btn"
              onClick={handleRequestOtp}
              disabled={loading}
              data-testid="applicant-otp-request"
            >
              Request OTP
            </Button>

            <label>OTP Code</label>
            <Input
              value={otpCode}
              onChange={(event) => setOtpCode(event.target.value)}
              className="auth-input"
              data-testid="applicant-otp-code"
              disabled={!otpIssued}
            />

            <Button className="auth-submit-btn" type="submit" disabled={loading || !otpIssued} data-testid="applicant-otp-submit">
              {loading ? "Please wait..." : "Login with OTP"}
            </Button>
          </form>
        ) : null}

        {tab === "register" ? (
          <form onSubmit={handleRegister} className="auth-form" data-testid="applicant-register-form">
            <h1 className="auth-title">Applicant Registration</h1>
            <p className="auth-subtitle">Create your account to file and track complaints</p>

            <label>Full Name</label>
            <Input
              value={registerForm.full_name}
              onChange={(event) => setRegisterForm((prev) => ({ ...prev, full_name: event.target.value }))}
              className="auth-input"
              data-testid="applicant-register-name"
            />

            <label>Mobile Number</label>
            <Input
              value={registerForm.mobile_number}
              onChange={(event) => setRegisterForm((prev) => ({ ...prev, mobile_number: event.target.value }))}
              className="auth-input"
              data-testid="applicant-register-mobile"
            />

            <label>Email</label>
            <Input
              value={registerForm.email}
              onChange={(event) => setRegisterForm((prev) => ({ ...prev, email: event.target.value }))}
              className="auth-input"
              data-testid="applicant-register-email"
            />

            <label>Password</label>
            <Input
              type="password"
              value={registerForm.password}
              onChange={(event) => setRegisterForm((prev) => ({ ...prev, password: event.target.value }))}
              className="auth-input"
              data-testid="applicant-register-password"
            />

            <Button className="auth-submit-btn" type="submit" disabled={loading} data-testid="applicant-register-submit">
              {loading ? "Please wait..." : "Register"}
            </Button>
          </form>
        ) : null}
      </div>
    </div>
  );
};
